<!--
  Cash-Out Refi Scenario — ported from the cash-out mode of CGS2022
  Scripts/Calculator/CalculatorV2.vue, restyled onto the calculator kit so it reads as a
  sibling of the Debt Consolidation calculator.

  THE IDEA
  Look up a borrower's loan, pull its liabilities from Encompass, tick the ones to pay off,
  set the new loan's terms, and compare three futures side by side: what they pay today,
  refinancing and paying the debts off, and refinancing without touching them.

  WHAT CAME ACROSS UNCHANGED
  Every formula and constant: the revolving-minimum estimate (19% APR + 1% of balance), the
  refi up-front fee rates, FHA/USDA monthly MI, the LTV room at 80 and 90, the three-way
  comparison, and the rule that the mortgage chosen as the refi loan is excluded from every
  total so its balance is not counted twice.

  WHAT CHANGED, AND WHY
  - The loan lookup was a Teleported modal flyout (LoanLookupFlyout.vue). Here it is simply
    step 1 of the wizard, because in a four-step wizard a modal over step 1 is a door in front
    of a door.
  - `ajaxManager` (a global jQuery FIFO queue reading a `token` cookie) is replaced by
    api/calculator.service.js on MSAL. The endpoint PATHS are byte-for-byte the CGS ones.
  - Kendo inputs -> the kit's plain inputs, matching the other calculators.
  - Dropped as dead on this path: the mode-select cards, the quick four-scenario mode, the MI
    rate dialog, the contact-scoped save/load, the Kendo Charts imports (registered but never
    rendered), and the old right-to-left note-rate keystroke machinery.

  UNITS — the one thing that silently produces plausible-but-wrong numbers:
  noteRate is a PERCENT here (7.500), matching CalculatorV2. Every payment formula divides by
  100. `term` is MONTHS everywhere.
-->
<template>
  <div class="calc-page">
    <PageHeaderPortal>
      <div class="calc-header">
        <div class="calc-header__icon">
          <i class="fa-duotone fa-money-bill-transfer"></i>
        </div>
        <div>
          <h1 class="calc-header__title">Cash-Out Refi Scenario</h1>
          <p class="calc-header__sub">Pull a loan's liabilities, choose what to pay off, and compare the new payment</p>
        </div>
      </div>
    </PageHeaderPortal>

    <!-- progress ------------------------------------------------------------- -->
    <section class="page-card">
      <div class="cw-progress-top">
        <span class="cw-progress-step">{{ isReview ? 'Complete' : `Step ${stepIndex + 1} of ${STEPS.length}` }}</span>
        <span class="cw-progress-pct">{{ isReview ? 'Scenario ready' : `${percent}% complete` }}</span>
      </div>
      <div class="cw-track" role="progressbar" :aria-valuenow="percent" aria-valuemin="0" aria-valuemax="100">
        <div class="cw-fill" :style="{ width: percent + '%' }"></div>
      </div>
      <ol class="cw-steps">
        <li v-for="(s, i) in STEPS" :key="s.key"
            :class="{ 'is-current': i === stepIndex, 'is-visited': i <= maxVisited }">
          <button type="button" :disabled="i > maxVisited" @click="goTo(i)">{{ i + 1 }}. {{ s.label }}</button>
        </li>
      </ol>
    </section>

    <div class="calc-body">
      <section class="page-card step-card">

        <!-- ══ STEP 1 — look up the loan ══════════════════════════════════════ -->
        <template v-if="step === 'lookup'">
          <div class="step-head">
            <span class="step-icon tone-blue"><i class="fa-light fa-magnifying-glass-dollar"></i></span>
            <div class="step-head-text">
              <p class="eyebrow">Step One</p>
              <h2>Look up the loan</h2>
              <p class="step-desc">
                Enter a loan number, or pick one from the pipeline. Encompass supplies the
                borrower's liabilities so you don't have to type them in.
              </p>
            </div>
          </div>

          <div class="field-grid">
            <label class="field">
              <span class="field-label">Loan number</span>
              <span class="input-shell">
                <input type="text" autocomplete="off" placeholder="e.g. 2505497190"
                       v-model.trim="loanNumber" @keyup.enter="loadLiabilities()" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">Prepared for</span>
              <span class="input-shell">
                <input type="text" autocomplete="off" placeholder="Borrower name" v-model="borrowerName" />
              </span>
            </label>

            <div class="field">
              <span class="field-label">&nbsp;</span>
              <button type="button" class="primary-button cw-load-btn"
                      :disabled="!loanNumber && !loanGuid || isLoading"
                      @click="loadLiabilities()">
                {{ isLoading ? 'Loading...' : 'Load Liabilities' }}
              </button>
            </div>
          </div>

          <div v-if="isLoading" class="cw-loader">
            <div class="cw-loader-track"><div class="cw-loader-fill" :style="{ width: loadProgress + '%' }"></div></div>
            <span>{{ loadStatus }}</span>
          </div>

          <div v-if="loadError" class="calc-note calc-note--error cw-note">
            <i class="fa-duotone fa-triangle-exclamation"></i>
            <div><strong>Couldn't load that loan</strong>{{ loadError }}</div>
          </div>

          <div v-else-if="liabilities.length && !isLoading" class="calc-note cw-note">
            <i class="fa-duotone fa-circle-check"></i>
            <div>
              <strong>Loaded loan {{ loanNumber }}</strong>
              {{ liabilities.length }} liabilit{{ liabilities.length === 1 ? 'y' : 'ies' }} found.
              Mortgages arrive unchecked so a balance you are already refinancing is not counted twice.
            </div>
          </div>

          <!-- pipeline picker -->
          <div class="cw-lookup">
            <div class="cw-tabs">
              <button type="button" class="cw-tab" :class="{ 'is-active': lookupTab === 'recent' }"
                      @click="openRecent">Recent Loans</button>
              <button type="button" class="cw-tab" :class="{ 'is-active': lookupTab === 'search' }"
                      @click="lookupTab = 'search'">Search Pipeline</button>
            </div>

            <div v-if="lookupTab === 'search'" class="cw-search">
              <span class="input-shell">
                <i class="fa-light fa-magnifying-glass affix"></i>
                <input type="text" autocomplete="off" class="cw-search-input"
                       placeholder="Loan number, borrower name..." :value="searchTerm"
                       @input="onSearchInput($event.target.value)" />
              </span>
            </div>

            <p v-if="lookupBusy" class="cw-lookup-msg">Searching...</p>
            <p v-else-if="lookupError" class="cw-lookup-msg cw-lookup-msg--error">{{ lookupError }}</p>
            <p v-else-if="!lookupRows.length" class="cw-lookup-msg">
              {{ lookupTab === 'recent' ? 'No recent loans.' : (searchTerm ? 'Nothing matched.' : 'Type to search your pipeline.') }}
            </p>

            <div v-else class="cw-rows">
              <table class="calc-table">
                <thead>
                  <tr>
                    <th>Loan #</th><th>Borrower</th><th>Loan officer</th>
                    <th>Amount</th><th>Purpose</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in lookupRows" :key="row.loanGuid || i">
                    <td>{{ row.loanNumber || '—' }}</td>
                    <td>{{ borrowerLabel(row) }}</td>
                    <td>{{ row.loanOfficer || '—' }}</td>
                    <td>{{ row.loanAmount ? money(num(row.loanAmount)) : '—' }}</td>
                    <td>{{ row.loanPurpose || '—' }}</td>
                    <td>{{ row.status || '—' }}</td>
                    <td><button type="button" class="link-button" @click="selectLoan(row)">Use this loan</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <!-- ══ STEP 2 — liabilities ═══════════════════════════════════════════ -->
        <template v-else-if="step === 'liabilities'">
          <div class="step-head">
            <span class="step-icon tone-violet"><i class="fa-light fa-list-check"></i></span>
            <div class="step-head-text">
              <p class="eyebrow">Step Two</p>
              <h2>Choose what to pay off</h2>
              <p class="step-desc">
                Tick the debts the new loan will clear. The mortgage you mark as the refi loan
                is excluded from the totals — its balance is already the Amount Owed.
              </p>
            </div>
          </div>

          <div class="field-grid">
            <label class="field">
              <span class="field-label">Loan type</span>
              <span class="input-shell input-shell--select">
                <select v-model="loanType">
                  <option v-for="t in LOAN_TYPES" :key="t.key" :value="t.key">{{ t.label }}</option>
                </select>
              </span>
            </label>

            <label class="field">
              <span class="field-label">Estimated value</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="estimatedValue" @blur="estimatedValue = money2(estimatedValue)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">
                Amount owed
                <span v-if="mortgageRows.length" class="cw-badge" :title="mortgageTooltip">
                  from {{ mortgageRows.length }} mortgage{{ mortgageRows.length === 1 ? '' : 's' }}
                </span>
              </span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="amountOwed" @blur="amountOwed = money2(amountOwed)" />
              </span>
            </label>
          </div>

          <!-- current payment -->
          <div class="cw-sub">
            <h3>The borrower's mortgage payment today</h3>
            <label class="check">
              <input type="checkbox" v-model="paymentIncludesEscrow" @change="refreshCurrentPandi" />
              <span>The refi loan's payment includes taxes &amp; insurance</span>
            </label>
          </div>

          <div class="field-grid">
            <label class="field">
              <span class="field-label">Current P &amp; I</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="currentPandi" @input="onCurrentPandi($event.target.value)"
                       @blur="currentPandi = money2(currentPandi)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Current taxes</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="currentTaxes" @blur="currentTaxes = money2(currentTaxes); refreshCurrentPandi()" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Current insurance</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="currentInsurance" @blur="currentInsurance = money2(currentInsurance); refreshCurrentPandi()" />
              </span>
            </label>
            <label class="field field--new-row">
              <span class="field-label">Current HOA</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="currentHoa" @blur="currentHoa = money2(currentHoa)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Current MI</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="currentMI" @blur="currentMI = money2(currentMI)" />
              </span>
            </label>
          </div>

          <dl class="compare-rows cw-current-totals">
            <div><dt>Current housing payment</dt><dd>{{ money(currentMonthlyPayment) }}</dd></div>
            <div>
              <dt>+ Monthly payments on the {{ otherLiabilities.length }} liabilities listed</dt>
              <dd>{{ money(totalLiabilitiesMonthly) }}</dd>
            </div>
            <div><dt><strong>= All-in payment today</strong></dt><dd>{{ money(currentAllInToday) }}</dd></div>
          </dl>

          <!-- liabilities -->
          <div class="cw-sub">
            <h3>Liabilities</h3>
            <div class="cw-sub-actions">
              <button type="button" class="link-button" @click="addLiability">+ Add liability</button>
              <button type="button" class="link-button" :disabled="!liabilities.length" @click="selectAll(true)">Select all</button>
              <button type="button" class="link-button" :disabled="!liabilities.length" @click="selectAll(false)">Select none</button>
            </div>
          </div>

          <p v-if="!liabilities.length" class="cw-lookup-msg">
            No liabilities yet. Load a loan on step one, or add them by hand.
          </p>

          <div v-else class="cw-liab">
            <div class="cw-liab-head">
              <span>Pay off</span><span>Creditor</span><span>Account type</span>
              <span>Unpaid balance</span><span>Monthly payment</span><span>Refi loan</span>
            </div>
            <div v-for="(l, i) in liabilities" :key="i" class="cw-liab-row" :class="{ 'is-primary': primaryIndex === i }">
              <span class="cw-liab-check">
                <input type="checkbox" v-model="l.payoff" :disabled="primaryIndex === i" />
              </span>
              <span class="input-shell">
                <input type="text" class="cw-text" autocomplete="off" placeholder="Creditor name" v-model="l.companyName" />
              </span>
              <span class="input-shell input-shell--select">
                <select v-model="l.accountType" @change="applyRevolvingDefault(l)">
                  <option value="">Account type...</option>
                  <option v-for="t in accountTypeOptions" :key="t" :value="t">{{ t }}</option>
                </select>
              </span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="l.unpaidBalance" @input="applyRevolvingDefault(l)"
                       @blur="l.unpaidBalance = money2(l.unpaidBalance)" />
              </span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="l.monthlyAmount" @input="l.paymentAuto = false"
                       @blur="l.monthlyAmount = money2(l.monthlyAmount)" />
              </span>
              <span class="cw-liab-actions">
                <button v-if="isMortgage(l.accountType)" type="button" class="link-button"
                        @click="setPrimary(i)">
                  {{ primaryIndex === i ? '✓ Refi loan' : 'Use as refi loan' }}
                </button>
                <button type="button" class="link-button cw-remove" title="Remove" @click="removeLiability(i)">&times;</button>
              </span>
            </div>
          </div>

          <dl v-if="liabilities.length" class="compare-rows cw-current-totals">
            <div><dt>Selected for payoff — adds to the new loan</dt><dd>{{ money(totalPayoffAmount) }}</dd></div>
            <div><dt>Monthly payments that go away</dt><dd>{{ money(payoffLiabilitiesMonthly) }}</dd></div>
            <div><dt>Monthly payments the borrower keeps</dt><dd>{{ money(remainingLiabilitiesMonthly) }}</dd></div>
          </dl>
        </template>

        <!-- ══ STEP 3 — terms ═════════════════════════════════════════════════ -->
        <template v-else-if="step === 'terms'">
          <div class="step-head">
            <span class="step-icon tone-teal"><i class="fa-light fa-file-signature"></i></span>
            <div class="step-head-text">
              <p class="eyebrow">Step Three</p>
              <h2>Set the new loan's terms</h2>
              <p class="step-desc">
                The new loan starts at the amount owed plus everything being paid off. Add cash
                to the borrower on top.
              </p>
            </div>
          </div>

          <div class="field-grid">
            <label class="field">
              <span class="field-label">Cash out to the borrower</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="cashOut" @blur="cashOut = money2(cashOut)" />
              </span>
            </label>
          </div>

          <div class="cw-ltv">
            <button type="button" class="cw-ltv-card" :class="{ 'is-used': isUsingRoom(cashRoom80) }"
                    :disabled="cashRoom80 <= 0" @click="useRoom(cashRoom80)">
              <span class="cw-ltv-label">Cash available at 80% LTV</span>
              <strong>{{ money(cashRoom80) }}</strong>
              <small>max loan {{ money(maxLoan80) }} &middot; {{ isUsingRoom(cashRoom80) ? 'using this' : 'click to use' }}</small>
            </button>
            <button type="button" class="cw-ltv-card" :class="{ 'is-used': isUsingRoom(cashRoom90) }"
                    :disabled="cashRoom90 <= 0" @click="useRoom(cashRoom90)">
              <span class="cw-ltv-label">Cash available at 90% LTV</span>
              <strong>{{ money(cashRoom90) }}</strong>
              <small>max loan {{ money(maxLoan90) }} &middot; {{ isUsingRoom(cashRoom90) ? 'using this' : 'click to use' }}</small>
            </button>
            <div class="cw-ltv-card cw-ltv-card--static"
                 :class="{ 'is-warn': ltv > 80 && ltv <= 90, 'is-over': ltv > 90 }">
              <span class="cw-ltv-label">This scenario's LTV</span>
              <strong>{{ pct(ltv) }}</strong>
              <small>{{ money(loanAmount) }} on {{ money(num(estimatedValue)) }}</small>
            </div>
          </div>

          <p v-if="!num(estimatedValue)" class="cw-lookup-msg">
            Enter an Estimated Value on step two to see how much room there is.
          </p>
          <p v-else-if="ltv > 90" class="cw-lookup-msg cw-lookup-msg--error">
            Over the 90% cap. Reduce the cash out by {{ money(loanAmount - maxLoan90) }}.
          </p>
          <p v-else-if="ltv > 80" class="cw-lookup-msg cw-lookup-msg--warn">
            Above 80% LTV. Reduce the cash out by {{ money(loanAmount - maxLoan80) }} to stay under it.
          </p>
          <p v-if="upFrontFee > 0" class="cw-lookup-msg">
            The {{ upFrontFeeLabel }} of {{ money(upFrontFee) }} is financed on top, so the
            cash-available figures above sit slightly higher than the final LTV.
          </p>

          <div class="field-grid">
            <label v-if="loanType === 'va'" class="field">
              <span class="field-label">VA type</span>
              <span class="input-shell input-shell--select">
                <select v-model="vaType">
                  <option v-for="v in VA_TYPES" :key="v" :value="v">{{ v }}</option>
                </select>
              </span>
            </label>
            <label class="field">
              <span class="field-label">Amortization type</span>
              <span class="input-shell input-shell--select">
                <select v-model="loanAmort">
                  <option v-for="a in AMORT_TYPES" :key="a" :value="a">{{ a }}</option>
                </select>
              </span>
            </label>
            <label class="field">
              <span class="field-label">Note rate</span>
              <span class="input-shell">
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="noteRatePct" @blur="noteRatePct = rate3(noteRatePct)" />
                <span class="affix affix--post">%</span>
              </span>
            </label>
            <label class="field field--new-row">
              <span class="field-label">Term</span>
              <span class="input-shell">
                <input type="text" inputmode="numeric" autocomplete="off" v-model="term" />
                <span class="affix affix--post">months</span>
              </span>
            </label>
            <label class="field">
              <span class="field-label">Fees for APR</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="additionalFees" @blur="additionalFees = money2(additionalFees)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Monthly taxes</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="taxes" @blur="taxes = money2(taxes)" />
              </span>
            </label>
            <label class="field field--new-row">
              <span class="field-label">Monthly insurance</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="insurance" @blur="insurance = money2(insurance)" />
              </span>
            </label>
            <label class="field">
              <span class="field-label">Monthly HOA</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="hoa" @blur="hoa = money2(hoa)" />
              </span>
            </label>
            <label v-if="loanType === 'conv'" class="field">
              <span class="field-label">Monthly MI</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="monthlyMI" @blur="monthlyMI = money2(monthlyMI)" />
              </span>
            </label>
          </div>

          <div v-if="calcError" class="calc-note calc-note--error cw-note">
            <i class="fa-duotone fa-triangle-exclamation"></i>
            <div><strong>That won't amortize</strong>{{ calcError }}</div>
          </div>
        </template>

        <!-- ══ STEP 4 — review ════════════════════════════════════════════════ -->
        <template v-else>
          <div class="step-head">
            <span class="step-icon tone-green"><i class="fa-light fa-circle-check"></i></span>
            <div class="step-head-text">
              <p class="eyebrow">Review</p>
              <h2>Three ways this could go</h2>
              <p class="step-desc">
                What the borrower pays today, what they'd pay after refinancing and clearing the
                selected debts, and what a refinance alone would do.
              </p>
            </div>
          </div>

          <div class="cw-banner" :class="{ 'is-negative': monthlySavings < 0 }">
            <span>{{ monthlySavings < 0 ? 'Estimated monthly increase' : 'Estimated monthly savings' }}</span>
            <strong>{{ money(Math.abs(monthlySavings)) }}</strong>
            <small>versus what the borrower pays today across the mortgage and the paid-off liabilities</small>
          </div>

          <div class="cw-compare">
            <div v-for="col in comparisonColumns" :key="col.key" class="cw-col" :class="{ 'is-best': col.isBest }">
              <p class="eyebrow">{{ col.eyebrow }}</p>
              <h4>{{ col.label }}</h4>
              <div class="stat-box"><span>Total monthly</span><strong>{{ money(col.total) }}</strong></div>
              <dl class="compare-rows">
                <div v-for="line in col.lines" :key="line.label">
                  <dt>{{ line.label }}</dt><dd>{{ money(line.value) }}</dd>
                </div>
              </dl>
              <p class="cw-delta" :class="col.deltaClass">{{ col.deltaLabel }}</p>
            </div>
          </div>

          <dl class="compare-rows cw-current-totals">
            <div><dt>Payoff amount rolled into the loan</dt><dd>{{ money(totalPayoffAmount) }}</dd></div>
            <div><dt>New loan amount</dt><dd>{{ money(loanAmount) }} at {{ pct(ltv) }} LTV</dd></div>
            <div><dt>Note rate / term</dt><dd>{{ pct(num(noteRatePct)) }} &middot; {{ termMonths }} months</dd></div>
            <div><dt>APR</dt><dd>{{ pct(apr) }}</dd></div>
          </dl>

          <div v-if="payoffRows.length" class="cw-payoff">
            <h3>Liabilities being paid off ({{ payoffRows.length }})</h3>
            <table class="calc-table">
              <thead>
                <tr><th>Creditor</th><th>Type</th><th>Balance paid off</th><th>Was paying / mo</th></tr>
              </thead>
              <tbody>
                <tr v-for="(l, i) in payoffRows" :key="i">
                  <td>{{ l.companyName || '—' }}</td>
                  <td>{{ l.accountType || '—' }}</td>
                  <td>{{ money(num(l.unpaidBalance)) }}</td>
                  <td>{{ num(l.monthlyAmount) ? money(num(l.monthlyAmount)) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- footer -->
        <div class="step-foot">
          <button type="button" class="secondary-button" :disabled="stepIndex === 0" @click="back">Back</button>
          <div class="foot-right">
            <button v-if="isReview" type="button" class="secondary-button" @click="copySummary">
              {{ copied ? 'Copied' : 'Copy Summary' }}
            </button>
            <button v-if="isReview" type="button" class="primary-button" :disabled="pdfBusy" @click="generatePdf">
              {{ pdfBusy ? 'Generating...' : 'Generate PDF' }}
            </button>
            <button v-if="isReview" type="button" class="secondary-button" @click="startOver">Start Over</button>
            <button v-else type="button" class="primary-button" :disabled="!canContinue" @click="next">
              {{ nextLabel }}
            </button>
          </div>
        </div>
      </section>

      <!-- rail ---------------------------------------------------------------- -->
      <aside class="rail--dark">
        <div>
          <p class="eyebrow eyebrow--gold">New Payment</p>
          <h3>Consolidated {{ loanTypeLabel }} refinance</h3>
        </div>

        <div class="rail-hero">
          <span>Estimated monthly payment</span>
          <strong>{{ isPriced ? money(totalPayment) : '&mdash;' }}</strong>
        </div>

        <dl class="rail-rows">
          <div><dt>Principal &amp; interest</dt><dd>{{ isPriced ? money(pandi) : '&mdash;' }}</dd></div>
          <div><dt>Mortgage insurance</dt><dd>{{ money(pmi) }}</dd></div>
          <div><dt>Taxes</dt><dd>{{ money(num(taxes)) }}</dd></div>
          <div><dt>Insurance</dt><dd>{{ money(num(insurance)) }}</dd></div>
          <div><dt>HOA</dt><dd>{{ money(num(hoa)) }}</dd></div>
        </dl>

        <dl class="rail-rows rail-rows--divided">
          <div><dt>Amount owed</dt><dd>{{ money(num(amountOwed)) }}</dd></div>
          <div><dt>Debts paid off</dt><dd>{{ money(totalPayoffAmount) }}</dd></div>
          <div><dt>Cash to borrower</dt><dd>{{ money(num(cashOut)) }}</dd></div>
          <div v-if="upFrontFee > 0"><dt>{{ upFrontFeeLabel }}</dt><dd>{{ money(upFrontFee) }}</dd></div>
          <div><dt>New loan amount</dt><dd>{{ money(loanAmount) }}</dd></div>
          <div><dt>LTV</dt><dd>{{ pct(ltv) }}</dd></div>
        </dl>

        <div class="rail-highlight">
          <span>{{ !isPriced ? 'Saved per month' : (monthlySavings < 0 ? 'More per month' : 'Saved per month') }}</span>
          <strong>{{ isPriced ? money(Math.abs(monthlySavings)) : '&mdash;' }}</strong>
        </div>
      </aside>
    </div>

    <CalculatorPdfDialog :open="pdfOpen"
                         :busy="pdfBusy"
                         :error="pdfError"
                         :blob-url="pdfBlobUrl"
                         :hosted-url="pdfHostedUrl"
                         :title="loanNumber ? `Loan ${loanNumber}` : 'Cash-out refi scenario'"
                         :file-name="`Cash_Out_Refi_${loanNumber || 'Scenario'}.pdf`"
                         @close="closePdf" />
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import auth from "@/authConfig.js"
  import calculatorService, { pick, unwrap } from "@/api/calculator.service.js"
  import CalculatorPdfDialog from "@/components/CalculatorPdfDialog.vue"
  import { buildPdfPayload, generatePdf } from "@/helpers/calculatorPdf.js"

  function num (v) {
    const n = parseFloat(String(v == null ? "" : v).replace(/[^0-9.-]/g, ""))
    return Number.isFinite(n) ? n : 0
  }
  function hasValue (v) {
    return String(v == null ? "" : v).trim() !== ""
  }
  function isBlankAmount (v) {
    return !hasValue(v) || num(v) === 0
  }

  const STEPS = [
    { key: "lookup", label: "Look Up Loan" },
    { key: "liabilities", label: "Select Payoff Liabilities" },
    { key: "terms", label: "Loan Terms" },
    { key: "review", label: "Review Scenario" }
  ]

  const LOAN_TYPES = [
    { key: "conv", label: "Conventional" },
    { key: "fha", label: "FHA" },
    { key: "va", label: "VA" },
    { key: "usda", label: "USDA" }
  ]
  const VA_TYPES = ["Regular Military/Active Duty", "Reserves", "National Guard", "Exempt"]
  const AMORT_TYPES = ["Fixed", "Adjustable Rate", "Graduated Payment", "Growing Equity Mortgage", "Other"]
  const ACCOUNT_TYPES = ["MortgageLoan", "HELOC", "Revolving", "Installment", "OtherLiability", "Lease", "Open"]

  // Refi up-front fee rates, copied from CalculatorV2.calculateUpFrontFees().
  const FHA_UPFRONT_REFI = 0.0001
  const USDA_UPFRONT = 0.01
  const VA_REFI_RATE = 0.005
  const FHA_MONTHLY_REFI = 0.0055
  const USDA_MONTHLY = 0.0035

  // The revolving minimum-payment proxy, verbatim: a month of interest at 19% APR plus 1% of
  // the balance. Encompass often returns a blank payment on a credit line, and a blank there
  // silently understates what the borrower actually pays today.
  const REVOLVING_APR = 0.19

  // What CGS's fake progress bar says while the liabilities load, in order, 650ms apart.
  const LOAD_MESSAGES = [
    "Connecting to Encompass...",
    "Pulling the loan file...",
    "Gathering the borrower's liabilities...",
    "Checking balances for accuracy...",
    "Sorting mortgages from revolving debt...",
    "Tidying up the list..."
  ]

  // Encompass returns these, but they are not debts a refinance can pay off.
  function isExcludedType (t) {
    const n = String(t || "").toLowerCase().replace(/[^a-z]/g, "")
    return ["jobrelated", "alimony", "childsupport", "separatemaintenance"].some(x => n.includes(x))
  }

  function pmt (principal, annualPct, months) {
    const r = annualPct / 100 / 12
    if (!(principal > 0 && months > 0)) return 0
    if (r <= 0) return principal / months
    const pow = Math.pow(1 + r, months)
    return (principal * r * pow) / (pow - 1)
  }

  export default {
    name: "CashOutRefiWizard",
    components: { PageHeaderPortal, CalculatorPdfDialog },

    data () {
      return {
        STEPS,
        LOAN_TYPES,
        VA_TYPES,
        AMORT_TYPES,
        stepIndex: 0,
        maxVisited: 0,

        loanNumber: "",
        loanGuid: "",
        borrowerName: "",
        isLoading: false,
        loadError: "",
        loadStatus: "",
        loadProgress: 0,
        loadTimer: null,

        lookupTab: "recent",
        lookupRows: [],
        lookupBusy: false,
        lookupError: "",
        searchTerm: "",
        searchTimer: null,
        searchToken: 0,
        recentLoaded: false,

        liabilities: [],
        primaryIndex: null,

        loanType: "conv",
        vaType: "Regular Military/Active Duty",
        loanAmort: "Fixed",
        estimatedValue: "",
        amountOwed: "",
        cashOut: "",
        noteRatePct: "",
        term: "360",
        taxes: "",
        insurance: "",
        hoa: "",
        monthlyMI: "",
        additionalFees: "",

        // Mirrors the refi loan's payment until the user types over it; clearing the field
        // hands control back. Same "auto until overridden" contract as the credit-card
        // estimate on the debt-consolidation calculator.
        currentPandiAuto: true,
        paymentIncludesEscrow: true,
        currentPandi: "",
        currentTaxes: "",
        currentInsurance: "",
        currentHoa: "",
        currentMI: "",

        copied: false,
        pdfOpen: false,
        pdfBusy: false,
        pdfError: "",
        pdfBlobUrl: "",
        pdfHostedUrl: ""
      }
    },

    computed: {
      step () { return STEPS[this.stepIndex].key },
      isReview () { return this.step === "review" },
      percent () { return Math.round(((this.stepIndex + 1) / STEPS.length) * 100) },
      loanTypeLabel () { return (LOAN_TYPES.find(t => t.key === this.loanType) || {}).label || "" },
      termMonths () { return Math.round(num(this.term)) },

      accountTypeOptions () {
        const present = this.liabilities.map(l => l.accountType).filter(Boolean)
        return Array.from(new Set([...ACCOUNT_TYPES, ...present]))
      },

      // The mortgage marked as the refi loan is excluded from EVERY total: its balance is
      // already Amount Owed and its payment is already Current P&I. Counting it again is the
      // single easiest way to produce a scenario that looks great and is wrong.
      otherLiabilities () {
        return this.liabilities.filter((l, i) => i !== this.primaryIndex)
      },
      mortgageRows () {
        return this.liabilities.map((l, i) => ({ l, i })).filter(r => this.isMortgage(r.l.accountType))
      },
      mortgageTotal () {
        return this.mortgageRows.reduce((s, r) => s + num(r.l.unpaidBalance), 0)
      },
      mortgageTooltip () {
        if (!this.mortgageRows.length) return "No mortgage liabilities were found on this loan."
        return this.mortgageRows
          .map(r => `${r.l.companyName || "Unnamed"}: ${this.money(num(r.l.unpaidBalance))}`)
          .concat(`= ${this.money(this.mortgageTotal)}`).join("\n")
      },
      payoffRows () { return this.otherLiabilities.filter(l => l.payoff) },
      totalPayoffAmount () { return this.payoffRows.reduce((s, l) => s + num(l.unpaidBalance), 0) },
      payoffLiabilitiesMonthly () { return this.payoffRows.reduce((s, l) => s + num(l.monthlyAmount), 0) },
      remainingLiabilitiesMonthly () {
        return this.otherLiabilities.filter(l => !l.payoff).reduce((s, l) => s + num(l.monthlyAmount), 0)
      },
      totalLiabilitiesMonthly () {
        return this.otherLiabilities.reduce((s, l) => s + num(l.monthlyAmount), 0)
      },

      currentMonthlyPayment () {
        return num(this.currentPandi) + num(this.currentTaxes) + num(this.currentInsurance) +
               num(this.currentHoa) + num(this.currentMI)
      },
      currentAllInToday () { return this.currentMonthlyPayment + this.totalLiabilitiesMonthly },

      // ── the new loan ──
      baseLoanAmount () { return num(this.amountOwed) + num(this.cashOut) + this.totalPayoffAmount },
      upFrontFee () {
        const base = this.baseLoanAmount
        if (base <= 0) return 0
        if (this.loanType === "fha") return base * FHA_UPFRONT_REFI
        if (this.loanType === "usda") return base * USDA_UPFRONT
        if (this.loanType === "va") return this.vaType === "Exempt" ? 0 : base * VA_REFI_RATE
        return 0
      },
      upFrontFeeLabel () {
        if (this.loanType === "fha") return "Up-front MIP (financed)"
        if (this.loanType === "va") return "VA funding fee (financed)"
        if (this.loanType === "usda") return "USDA guarantee fee (financed)"
        return "Up-front fee"
      },
      loanAmount () { return this.baseLoanAmount + this.upFrontFee },
      ltv () {
        const v = num(this.estimatedValue)
        return v > 0 ? (this.loanAmount / v) * 100 : 0
      },

      maxLoan80 () { return num(this.estimatedValue) * 0.8 },
      maxLoan90 () { return num(this.estimatedValue) * 0.9 },
      // Room is measured on the loan before the financed fee, as CGS does. The note under the
      // cards says so, because for FHA/VA/USDA the final LTV lands a little higher.
      loanBeforeCashOut () { return num(this.amountOwed) + this.totalPayoffAmount },
      cashRoom80 () { return Math.max(0, this.maxLoan80 - this.loanBeforeCashOut) },
      cashRoom90 () { return Math.max(0, this.maxLoan90 - this.loanBeforeCashOut) },

      // An EMPTY rate field is not a 0% loan. pmt()'s r<=0 branch straight-lines the
      // principal over the term, which otherwise puts a confident P&I figure on the rail
      // before anyone has entered a rate. Typing an explicit 0 still gets the 0% treatment.
      isPriced () { return hasValue(this.noteRatePct) && this.termMonths > 0 },
      pandi () {
        if (!this.isPriced) return 0
        return pmt(this.loanAmount, num(this.noteRatePct), this.termMonths)
      },
      pmi () {
        const base = this.baseLoanAmount
        if (this.loanType === "fha") return (base * FHA_MONTHLY_REFI) / 12
        if (this.loanType === "usda") return (base * USDA_MONTHLY) / 12
        if (this.loanType === "va") return 0
        return num(this.monthlyMI)
      },
      totalPayment () {
        return this.pandi + this.pmi + num(this.taxes) + num(this.insurance) + num(this.hoa)
      },

      calcError () {
        if (!this.loanAmount) return ""
        if (this.termMonths <= 0) return "Enter a term in months."
        if (this.pandi > 0 && this.loanAmount * (num(this.noteRatePct) / 100 / 12) >= this.pandi) {
          return "The first month's interest is at least the whole payment, so the balance would never come down."
        }
        return ""
      },

      // ── the three-way comparison ──
      // Taxes, insurance, HOA and MI are deliberately held constant across the two refinance
      // variants: the only thing that differs is whether the payoff is rolled into the loan.
      pandiWithoutPayoff () {
        if (this.loanAmort !== "Fixed") return this.pandi
        return pmt(Math.max(0, this.loanAmount - this.totalPayoffAmount), num(this.noteRatePct), this.termMonths)
      },
      currentTotal () { return this.currentMonthlyPayment + this.totalLiabilitiesMonthly },
      withPayoffTotal () { return this.totalPayment + this.remainingLiabilitiesMonthly },
      withoutPayoffTotal () {
        return this.pandiWithoutPayoff + this.pmi + num(this.taxes) + num(this.insurance) +
               num(this.hoa) + this.totalLiabilitiesMonthly
      },
      monthlySavings () { return this.currentTotal - this.withPayoffTotal },

      comparisonColumns () {
        const refiOnlyHousing = this.withoutPayoffTotal - this.totalLiabilitiesMonthly
        const cols = [
          {
            key: "today",
            eyebrow: "Current",
            label: "Paying Today",
            total: this.currentTotal,
            lines: [
              { label: "Current mortgage", value: this.currentMonthlyPayment },
              { label: "Liability payments", value: this.totalLiabilitiesMonthly }
            ]
          },
          {
            key: "withPayoff",
            eyebrow: "Option 1",
            label: "Refinance & Pay Off Debts",
            total: this.withPayoffTotal,
            lines: [
              { label: "New payment", value: this.totalPayment },
              { label: "Liabilities kept", value: this.remainingLiabilitiesMonthly }
            ]
          },
          {
            key: "withoutPayoff",
            eyebrow: "Option 2",
            label: "Refinance Only",
            total: this.withoutPayoffTotal,
            lines: [
              { label: "New payment", value: refiOnlyHousing },
              { label: "Liability payments", value: this.totalLiabilitiesMonthly }
            ]
          }
        ]
        const lowest = Math.min(...cols.map(c => c.total))
        for (const c of cols) {
          c.isBest = c.total === lowest
          const d = c.total - this.currentTotal
          if (c.key === "today") {
            c.deltaLabel = "what they pay now"
            c.deltaClass = ""
          } else if (Math.abs(d) < 0.5) {
            c.deltaLabel = "about the same"
            c.deltaClass = ""
          } else {
            c.deltaLabel = `${this.money(Math.abs(d))} ${d < 0 ? "less" : "more"} a month`
            c.deltaClass = d < 0 ? "is-down" : "is-up"
          }
        }
        return cols
      },

      apr () {
        const fees = num(this.additionalFees) + this.upFrontFee
        const financed = this.loanAmount - fees
        const payment = this.pandi
        const n = this.termMonths
        if (!(this.loanAmount > 0 && payment > 0 && n > 0 && financed > 0)) return 0
        let low = 0
        let high = 1
        let r = 0
        for (let i = 0; i < 200; i++) {
          r = (low + high) / 2
          if (r <= 0) break
          const pv = payment * ((1 - Math.pow(1 + r, -n)) / r)
          const diff = pv - financed
          if (Math.abs(diff) < 0.01) break
          if (diff > 0) low = r
          else high = r
        }
        return r * 12 * 100
      },

      canContinue () {
        if (this.step === "lookup") return true
        if (this.step === "liabilities") return num(this.amountOwed) > 0
        if (this.step === "terms") return num(this.noteRatePct) > 0 && this.termMonths > 0 && !this.calcError
        return true
      },
      nextLabel () {
        if (this.step === "lookup") return "Continue to Liabilities"
        if (this.step === "liabilities") return "Continue to Loan Terms"
        return "Review Scenario"
      }
    },

    beforeUnmount () {
      if (this.loadTimer) clearInterval(this.loadTimer)
      if (this.searchTimer) clearTimeout(this.searchTimer)
    },

    methods: {
      num,
      money (n) {
        return "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      pct (n) { return (Number(n) || 0).toFixed(3) + "%" },
      money2 (raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      rate3 (raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toFixed(3)
      },
      isMortgage (t) { return String(t || "").toLowerCase().includes("mortgage") },
      isRevolving (t) { return String(t || "").toLowerCase().includes("revolving") },
      borrowerLabel (row) {
        const n = `${row.borrowerFirst || ""} ${row.borrowerLast || ""}`.trim()
        return n || "(borrower name unavailable)"
      },

      async token () {
        const res = await auth.acquireTokenResponse()
        return res?.accessToken
      },
      azureId () {
        try { return localStorage.getItem("azure_id") || "" } catch (e) { return "" }
      },

      // ── step 1: lookup ──
      async openRecent () {
        this.lookupTab = "recent"
        if (this.recentLoaded) return
        this.lookupBusy = true
        this.lookupError = ""
        try {
          const res = await calculatorService.GetRecentLoans(this.azureId(), await this.token())
          const rows = unwrap(res) || []
          // Recent comes back as raw Encompass pipeline rows; search comes back flat. One
          // internal shape either way.
          this.lookupRows = rows.map(r => {
            const f = pick(r, "fields") || {}
            return {
              loanGuid: pick(r, "loanId", "loanGuid") || "",
              loanNumber: f["Loan.LoanNumber"] || "",
              borrowerFirst: f["Fields.4000"] || "",
              borrowerLast: f["Fields.4002"] || "",
              loanOfficer: f["Fields.317"] || "",
              loanAmount: f["Fields.2"] || "",
              loanPurpose: f["Fields.19"] || "",
              status: f["Log.MS.CurrentMilestone"] || f["Fields.1393"] || ""
            }
          })
          this.recentLoaded = true
        } catch (e) {
          this.lookupError = e?.message || "Couldn't load your recent loans."
          this.lookupRows = []
        } finally {
          this.lookupBusy = false
        }
      },

      onSearchInput (value) {
        this.searchTerm = value
        if (this.searchTimer) clearTimeout(this.searchTimer)
        if (!value || value.trim().length < 2) {
          this.lookupRows = []
          return
        }
        // Debounced, and every response carries the token it was issued with so a slow early
        // reply cannot overwrite a fast later one.
        this.searchTimer = setTimeout(() => this.runSearch(value.trim()), 350)
      },

      async runSearch (term) {
        const myToken = ++this.searchToken
        this.lookupBusy = true
        this.lookupError = ""
        try {
          const res = await calculatorService.SearchLoanPipeline(this.azureId(), term, await this.token())
          if (myToken !== this.searchToken) return
          const rows = unwrap(res) || []
          this.lookupRows = rows.map(r => ({
            loanGuid: pick(r, "loanId", "loanGuid") || "",
            loanNumber: pick(r, "loanNumber") || "",
            borrowerFirst: pick(r, "borrowerFirst") || "",
            borrowerLast: pick(r, "borrowerLast") || "",
            loanOfficer: pick(r, "loanOfficerName", "loanOfficer") || "",
            loanAmount: pick(r, "loanAmount") || "",
            loanPurpose: pick(r, "loanPurpose") || "",
            status: pick(r, "loanStatus", "status") || ""
          }))
        } catch (e) {
          if (myToken !== this.searchToken) return
          this.lookupError = e?.message || "That search failed."
          this.lookupRows = []
        } finally {
          if (myToken === this.searchToken) this.lookupBusy = false
        }
      },

      selectLoan (row) {
        this.loanNumber = row.loanNumber || ""
        this.loanGuid = row.loanGuid || ""
        if (!this.borrowerName) this.borrowerName = this.borrowerLabel(row)
        this.loadLiabilities()
      },

      startLoadAnimation () {
        let i = 0
        this.loadProgress = 8
        this.loadStatus = LOAD_MESSAGES[0]
        this.loadTimer = setInterval(() => {
          i = (i + 1) % LOAD_MESSAGES.length
          this.loadStatus = LOAD_MESSAGES[i]
          // Eases toward 90 and stops there; the response is what takes it to 100.
          this.loadProgress = Math.min(90, this.loadProgress + (90 - this.loadProgress) * 0.35)
        }, 650)
      },
      stopLoadAnimation () {
        if (this.loadTimer) clearInterval(this.loadTimer)
        this.loadTimer = null
      },

      async loadLiabilities () {
        if (!this.loanNumber && !this.loanGuid) return
        this.isLoading = true
        this.loadError = ""
        this.startLoadAnimation()
        try {
          const res = await calculatorService.GetLoanPayoffLiabilities(this.loanNumber, this.loanGuid, await this.token())
          const data = unwrap(res) || {}
          this.loanNumber = pick(data, "loanNumber") || this.loanNumber
          this.loanGuid = pick(data, "loanGuid") || this.loanGuid

          const rows = pick(data, "liabilities") || []
          this.primaryIndex = null
          this.liabilities = rows
            .filter(r => !isExcludedType(pick(r, "accountType")))
            .map(r => {
              const type = pick(r, "accountType") || ""
              const def = pick(r, "defaultPayoff")
              const l = {
                accountType: type,
                companyName: pick(r, "companyName") || "",
                unpaidBalance: this.money2(String(pick(r, "unpaidBalance") ?? "")),
                monthlyAmount: this.money2(String(pick(r, "monthlyAmount") ?? "")),
                // A mortgage never arrives ticked: its balance is about to become Amount Owed.
                payoff: this.isMortgage(type) ? false : (def === undefined ? true : !!def),
                paymentAuto: true
              }
              this.applyRevolvingDefault(l)
              return l
            })

          if (this.mortgageTotal > 0) this.amountOwed = this.money2(String(this.mortgageTotal))
          this.loadProgress = 100
          if (this.stepIndex === 0) this.goTo(1)
        } catch (e) {
          this.liabilities = []
          this.primaryIndex = null
          this.loadError = e?.response?.data?.message || e?.message || "Unable to load liabilities for that loan."
        } finally {
          this.stopLoadAnimation()
          this.isLoading = false
        }
      },

      // ── step 2: liabilities ──
      revolvingMinimum (balance) {
        const owed = num(balance)
        if (owed <= 0) return null
        return Math.round(owed * (REVOLVING_APR / 12) + owed * 0.01)
      },
      applyRevolvingDefault (l) {
        if (!this.isRevolving(l.accountType)) return
        if (l.paymentAuto === false && !isBlankAmount(l.monthlyAmount)) return
        const est = this.revolvingMinimum(l.unpaidBalance)
        if (est == null) return
        l.monthlyAmount = this.money2(String(est))
        l.paymentAuto = true
      },
      addLiability () {
        this.liabilities.push({
          accountType: "",
          companyName: "",
          unpaidBalance: "",
          monthlyAmount: "",
          payoff: true,
          paymentAuto: true,
          isManual: true
        })
      },
      removeLiability (i) {
        this.liabilities.splice(i, 1)
        if (this.primaryIndex === i) this.primaryIndex = null
        else if (this.primaryIndex != null && i < this.primaryIndex) this.primaryIndex -= 1
      },
      selectAll (on) {
        this.liabilities.forEach((l, i) => { if (i !== this.primaryIndex) l.payoff = on })
      },
      setPrimary (i) {
        if (this.primaryIndex === i) {
          this.primaryIndex = null
          this.amountOwed = this.mortgageTotal ? this.money2(String(this.mortgageTotal)) : ""
          return
        }
        this.primaryIndex = i
        this.liabilities[i].payoff = false
        this.amountOwed = this.money2(String(num(this.liabilities[i].unpaidBalance)))
        this.refreshCurrentPandi()
      },

      onCurrentPandi (raw) {
        this.currentPandi = raw
        this.currentPandiAuto = isBlankAmount(raw)
        if (this.currentPandiAuto) this.refreshCurrentPandi()
      },
      // Backs the escrows out of the refi loan's total payment to leave P&I, unless the user
      // has typed their own figure.
      refreshCurrentPandi () {
        if (!this.currentPandiAuto) return
        const primary = this.primaryIndex == null ? null : this.liabilities[this.primaryIndex]
        const payment = num(primary?.monthlyAmount)
        if (payment <= 0) return
        const pi = this.paymentIncludesEscrow
          ? payment - num(this.currentTaxes) - num(this.currentInsurance) - num(this.currentHoa)
          : payment
        this.currentPandi = this.money2(String(Math.max(0, Math.round(pi))))
      },

      // ── step 3 ──
      useRoom (amount) {
        if (amount > 0) this.cashOut = this.money2(String(Math.floor(amount)))
      },
      isUsingRoom (amount) {
        const f = Math.floor(amount)
        return f > 0 && num(this.cashOut) === f
      },

      // ── navigation ──
      goTo (i) {
        if (i > this.maxVisited) return
        this.stepIndex = i
        this.copied = false
      },
      next () {
        if (this.isReview) return
        this.stepIndex += 1
        this.maxVisited = Math.max(this.maxVisited, this.stepIndex)
      },
      back () {
        if (this.stepIndex === 0) return
        this.stepIndex -= 1
        this.copied = false
      },
      startOver () {
        Object.assign(this, this.$options.data.call(this))
      },


      // ── PDF ──────────────────────────────────────────────────────────────
      async generatePdf () {
        if (this.pdfBusy) return
        this.revokePdf()
        this.pdfOpen = true
        this.pdfBusy = true
        this.pdfError = ""
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const { blobUrl, hostedUrl } = await generatePdf({
            payload: this.pdfPayload(),
            token: tokenResponse?.accessToken
          })
          this.pdfBlobUrl = blobUrl
          this.pdfHostedUrl = hostedUrl
        } catch (e) {
          this.pdfError = e?.response?.data?.message || e?.message || "The PDF service did not respond."
        } finally {
          this.pdfBusy = false
        }
      },
      closePdf () {
        this.pdfOpen = false
        this.revokePdf()
      },
      // A blob URL holds the whole document in memory until it is revoked.
      revokePdf () {
        if (this.pdfBlobUrl) {
          try { URL.revokeObjectURL(this.pdfBlobUrl) } catch (e) { /* already gone */ }
        }
        this.pdfBlobUrl = ""
        this.pdfHostedUrl = ""
        this.pdfError = ""
      },
      // The cash-out block is what turns a plain payment PDF into the comparison sheet:
      // the template only renders those rows when PayoffAmount is non-zero.
      pdfPayload () {
        return buildPdfPayload({
          scenarios: [{
            name: `Cash-Out Refi ${this.loanTypeLabel}`,
            input: {
              purchasePrice: this.estimatedValue,
              downPayment: "",
              cashOut: this.cashOut
            },
            calc: {
              isBuy: false,
              termMonths: this.termMonths,
              annualPct: num(this.noteRatePct),
              loanAmount: this.loanAmount,
              pandi: this.pandi,
              apr: this.apr,
              taxes: num(this.taxes),
              insurance: num(this.insurance),
              hoa: num(this.hoa),
              pmi: this.pmi,
              totalPayment: this.totalPayment,
              totalInterestPaid: 0,
              ltv: this.ltv
            },
            cashOut: {
              payoffAmount: this.totalPayoffAmount,
              currentTotal: this.currentTotal,
              withPayoffTotal: this.withPayoffTotal,
              withoutPayoffTotal: this.withoutPayoffTotal,
              monthlySavings: this.monthlySavings,
              payoffLiabilities: this.payoffRows
            }
          }],
          context: {
            borrowerName: this.borrowerName,
            loanNumber: this.loanNumber,
            homeValue: num(this.estimatedValue),
            currentPandi: num(this.currentPandi),
            currentTaxesAndInsurance: num(this.currentTaxes) + num(this.currentInsurance),
            currentOtherDebts: this.totalLiabilitiesMonthly,
            payingTodayTotal: this.currentTotal,
            payoffTotal: this.totalPayoffAmount,
            payoffMonthlyTotal: this.payoffLiabilitiesMonthly,
            payoffLiabilities: this.payoffRows
          }
        })
      },

      async copySummary () {
        const l = [`Cash-Out Refi Scenario${this.loanNumber ? " — loan " + this.loanNumber : ""}`]
        if (this.borrowerName) l.push(`Prepared for ${this.borrowerName}`)
        l.push("", "TODAY")
        l.push(`  Mortgage payment: ${this.money(this.currentMonthlyPayment)}`)
        l.push(`  Liability payments: ${this.money(this.totalLiabilitiesMonthly)}`)
        l.push(`  All-in: ${this.money(this.currentTotal)}`)
        l.push("", "NEW LOAN")
        l.push(`  Amount owed: ${this.money(num(this.amountOwed))}`)
        l.push(`  Debts paid off: ${this.money(this.totalPayoffAmount)} (${this.payoffRows.length})`)
        l.push(`  Cash to borrower: ${this.money(num(this.cashOut))}`)
        if (this.upFrontFee > 0) l.push(`  ${this.upFrontFeeLabel}: ${this.money(this.upFrontFee)}`)
        l.push(`  Loan amount: ${this.money(this.loanAmount)} at ${this.pct(this.ltv)} LTV`)
        l.push(`  ${this.pct(num(this.noteRatePct))} for ${this.termMonths} months, APR ${this.pct(this.apr)}`)
        l.push(`  New payment: ${this.money(this.totalPayment)}`)
        l.push("", "COMPARISON")
        for (const c of this.comparisonColumns) {
          l.push(`  ${c.label}: ${this.money(c.total)}${c.isBest ? "  <= lowest" : ""}`)
        }
        l.push("", `  ${this.monthlySavings < 0 ? "Increase" : "Savings"}: ${this.money(Math.abs(this.monthlySavings))} a month`)
        try {
          await navigator.clipboard.writeText(l.join("\n"))
          this.copied = true
          setTimeout(() => (this.copied = false), 2000)
        } catch (e) {
          this.copied = false
        }
      }
    }
  }
</script>

<style scoped>
  /* Shared look comes from assets/css/calculatorKit.css; only this page's layout is here. */
  .cw-progress-top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 10px;
  }

  .cw-progress-step {
    font-size: var(--fs-md);
    font-weight: 800;
    color: var(--calc-navy);
  }

  .cw-progress-pct {
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-muted);
  }

  .cw-track {
    height: 9px;
    border-radius: 999px;
    background: #e9edf4;
    overflow: hidden;
  }

  .cw-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #dcb254, #b8912f);
    transition: width 0.25s ease;
  }

  .cw-steps {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 6px 18px;
    list-style: none;
    margin: 13px 0 0;
    padding: 0;
  }

  .cw-steps button {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    font-size: var(--fs-sm);
    font-weight: 600;
    color: var(--calc-dim);
    cursor: default;
  }

  .cw-steps .is-visited button {
    color: var(--calc-blue);
    cursor: pointer;
  }

  .cw-steps .is-visited button:hover { text-decoration: underline; }

  .cw-steps .is-current button {
    color: var(--calc-navy);
    font-weight: 800;
    cursor: default;
    text-decoration: none;
  }

  .cw-load-btn { width: 100%; }

  .cw-note { margin-top: 20px; }

  .cw-loader { margin-top: 20px; }

  .cw-loader-track {
    height: 6px;
    border-radius: 999px;
    background: #e9edf4;
    overflow: hidden;
  }

  .cw-loader-fill {
    height: 100%;
    background: var(--calc-blue);
    transition: width 0.4s ease;
  }

  .cw-loader span {
    display: block;
    margin-top: 8px;
    font-size: var(--fs-xs);
    color: var(--calc-muted);
  }

  /* ── inline pipeline picker (was a teleported flyout in CGS) ── */
  .cw-lookup {
    margin-top: 26px;
    border-top: 1px solid var(--calc-line);
    padding-top: 20px;
  }

  .cw-tabs {
    display: inline-flex;
    gap: 3px;
    padding: 3px;
    background: var(--calc-soft);
    border: 1px solid var(--calc-line);
    border-radius: 10px;
  }

  .cw-tab {
    border: 0;
    background: transparent;
    border-radius: 7px;
    padding: 8px 16px;
    font: inherit;
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-muted);
    cursor: pointer;
  }

  .cw-tab.is-active {
    background: var(--calc-navy);
    color: #ffffff;
  }

  .cw-search {
    margin-top: 14px;
    max-width: 420px;
  }

  .cw-search-input { text-align: left; }

  .cw-lookup-msg {
    margin: 14px 0 0;
    font-size: var(--fs-sm);
    color: var(--calc-muted);
  }

  .cw-lookup-msg--error { color: #a4472f; }
  .cw-lookup-msg--warn { color: var(--calc-gold); }

  .cw-rows {
    margin-top: 6px;
    max-height: 340px;
    overflow: auto;
    border: 1px solid var(--calc-line);
    border-radius: 9px;
  }

  /* ── sub-headings inside a step ── */
  .cw-sub {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 30px;
    padding-top: 18px;
    border-top: 1px solid var(--calc-line);
  }

  .cw-sub h3 {
    margin: 0;
    font-size: var(--fs-lg);
    font-weight: 700;
  }

  .cw-sub-actions {
    display: flex;
    gap: 16px;
  }

  .cw-current-totals { margin-top: 18px; }

  .cw-badge {
    font-size: var(--fs-2xs);
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
    color: var(--calc-gold);
    background: var(--calc-gold-bg);
    border: 1px solid var(--calc-gold-edge);
    border-radius: 999px;
    padding: 2px 8px;
    cursor: help;
  }

  /* ── liabilities grid ── */
  .cw-liab {
    margin-top: 16px;
    border: 1px solid var(--calc-line);
    border-radius: 9px;
    overflow: hidden;
  }

  .cw-liab-head,
  .cw-liab-row {
    display: grid;
    grid-template-columns: 62px minmax(0, 2fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) 150px;
    gap: 10px;
    align-items: center;
    padding: 10px 14px;
  }

  .cw-liab-head {
    background: var(--calc-soft);
    border-bottom: 1px solid var(--calc-line);
    font-size: var(--fs-2xs);
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--calc-muted);
  }

  .cw-liab-row { border-bottom: 1px solid #f1f4f9; }
  .cw-liab-row:last-child { border-bottom: 0; }

  .cw-liab-row.is-primary { background: var(--calc-gold-bg); }

  .cw-liab-check { text-align: center; }

  .cw-text { text-align: left; }

  .cw-liab-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .cw-remove {
    font-size: var(--fs-lg);
    color: var(--calc-dim);
  }

  .cw-remove:hover { color: #a4472f; }

  /* ── LTV cards ── */
  .cw-ltv {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
    margin-top: 22px;
  }

  .cw-ltv-card {
    text-align: left;
    border: 1px solid var(--calc-line);
    border-radius: 10px;
    background: var(--calc-card);
    padding: 16px;
    cursor: pointer;
    font: inherit;
  }

  .cw-ltv-card:hover:not(:disabled):not(.cw-ltv-card--static) {
    border-color: var(--calc-blue);
  }

  .cw-ltv-card:disabled { cursor: default; opacity: 0.6; }

  .cw-ltv-card--static { cursor: default; }

  .cw-ltv-card.is-used {
    border-color: var(--calc-navy);
    background: #f4f7fc;
  }

  .cw-ltv-card.is-warn { background: var(--calc-gold-bg); border-color: var(--calc-gold-edge); }
  .cw-ltv-card.is-over { background: #fdf0ec; border-color: #f3cec2; }

  .cw-ltv-label {
    display: block;
    font-size: var(--fs-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--calc-muted);
  }

  .cw-ltv-card strong {
    display: block;
    margin-top: 8px;
    font-size: var(--fs-xl);
    font-weight: 800;
  }

  .cw-ltv-card small {
    display: block;
    margin-top: 5px;
    font-size: var(--fs-2xs);
    color: var(--calc-muted);
  }

  /* ── review ── */
  .cw-banner {
    margin-top: 22px;
    background: linear-gradient(135deg, #10305e, #1b4a86);
    border-top: 3px solid var(--calc-gold-lt);
    border-radius: 10px;
    padding: 20px;
    color: #ffffff;
  }

  .cw-banner span {
    display: block;
    font-size: var(--fs-2xs);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #e9bd54;
  }

  .cw-banner strong {
    display: block;
    margin-top: 8px;
    font-size: var(--fs-4xl);
    font-weight: 800;
  }

  .cw-banner small {
    display: block;
    margin-top: 6px;
    font-size: var(--fs-xs);
    color: rgba(255, 255, 255, 0.66);
  }

  .cw-banner.is-negative strong { color: #f3b9a8; }

  .cw-compare {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-top: 16px;
  }

  .cw-col {
    border: 1px solid var(--calc-line);
    border-radius: 10px;
    padding: 18px;
    min-width: 0;
  }

  .cw-col.is-best {
    background: var(--calc-gold-bg);
    border-color: var(--calc-gold-edge);
  }

  .cw-col h4 {
    margin: 4px 0 0;
    font-size: var(--fs-lg);
    font-weight: 700;
  }

  .cw-delta {
    margin: 12px 0 0;
    font-size: var(--fs-sm);
    font-weight: 700;
    color: var(--calc-muted);
  }

  .cw-delta.is-down { color: #1f7a4d; }
  .cw-delta.is-up { color: #a4472f; }

  .cw-payoff { margin-top: 24px; }

  .cw-payoff h3 {
    margin: 0;
    font-size: var(--fs-lg);
    font-weight: 700;
  }

  @media (max-width: 1240px) {
    .cw-compare { grid-template-columns: minmax(0, 1fr); }
  }

  @media (max-width: 1000px) {
    .cw-ltv { grid-template-columns: minmax(0, 1fr); }

    /* The six-column liability row stops being a row and becomes a stack of labelled fields. */
    .cw-liab-head { display: none; }

    .cw-liab-row {
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
      padding: 14px;
      border-bottom: 1px solid var(--calc-line);
    }
  }
</style>
